import {
  type KsAppClientData,
  type KsRenderResults,
  type Except,
  type GenericResponse,
  type KsAppClientDataAndMeta,
  type KsAppClientDataNoMeta,
  type KsFileSaverParams,
} from '@knapsack/types';
import { fetcher, FetcherError } from '@knapsack/utils';
import urlJoin from 'url-join';
import type {
  PatternRenderData,
  KnapsackDataStoreSaveBody,
  EndpointPaths,
  EndpointFiles,
  ExtractEndpointData,
  ExtractEndpointResBody,
  EndpointPlugins,
  RouteDiscovery,
  RouteInspect,
} from '@knapsack/app/api-info';

export type { PatternRenderData, EndpointPaths };

export async function checkAppClientUrl(url: string): Promise<GenericResponse> {
  try {
    const theUrl = urlJoin(url, '/api/v1' satisfies EndpointPaths);
    const res = await fetcher<GenericResponse>({
      url: theUrl,
    });
    return res;
  } catch (err) {
    return {
      ok: false,
      message: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

export async function getDataStore({
  appClientUrl,
}: {
  appClientUrl: string;
}): Promise<GenericResponse<KsAppClientDataAndMeta>> {
  try {
    const data = await fetcher<KsAppClientDataAndMeta>({
      url: urlJoin(appClientUrl, '/api/v1/data-store' satisfies EndpointPaths),
    });

    return {
      ok: true,
      data,
    };
  } catch (err) {
    return {
      ok: false,
      message: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

// just need a function that can throw an error
export async function getAppClientData({
  appClientUrl,
}: {
  appClientUrl: string;
}): Promise<KsAppClientDataAndMeta> {
  const serverDataRes = await getDataStore({ appClientUrl });
  if (!serverDataRes.ok || !serverDataRes.data) {
    const message = `We're having trouble accessing your server at ${appClientUrl}.`;
    throw new Error(message);
  }
  return serverDataRes.data;
}

/** Sole job is to strip `metaState` if it's there */
export function prepAppClientData(
  state: KsAppClientData,
): KsAppClientDataNoMeta {
  const { metaState, ...appClientDataNoMeta } = state;
  return appClientDataNoMeta;
}

export async function prepareDataForFileSave({
  urlBase,
  state,
}: {
  urlBase: string;
  state: KsAppClientDataNoMeta;
}): Promise<GenericResponse<KsFileSaverParams>> {
  try {
    const body: KnapsackDataStoreSaveBody = {
      state: prepAppClientData(state),
    };
    const data = await fetcher<GenericResponse<KsFileSaverParams>>({
      url: urlJoin(urlBase, '/api/v1/data-store-prep' satisfies EndpointPaths),
      body,
    });

    return data;
  } catch (err) {
    if (err instanceof FetcherError) {
      // Preserve the upstream response so the caller can log the actual
      // headers/body of the failure instead of just "Error 500".
      return {
        ok: false,
        message: err.message,
        meta: {
          status: err.status,
          statusText: err.statusText,
          url: err.url,
          responseHeaders: err.responseHeaders,
          responseBody: err.responseBody,
        },
      };
    }
    return {
      ok: false,
      message: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

export async function submitDataForFileSave({
  urlBase,
  state,
}: {
  urlBase: string;
  state: KsAppClientDataNoMeta;
}): Promise<void> {
  const appClientData = prepAppClientData(state);
  const body: KnapsackDataStoreSaveBody = {
    state: appClientData,
  };
  const data = await fetcher<GenericResponse>({
    url: urlJoin(urlBase, '/api/v1/data-store' satisfies EndpointPaths),
    body,
  });

  if (!data.ok) {
    throw new Error(`Could not save data to localhost files: ${data.message}`);
  }
}

export async function getPluginContent({
  pluginId,
  appClientUrl,
}: {
  appClientUrl: string;
} & Except<ExtractEndpointData<EndpointPlugins>, 'type'>): Promise<
  ExtractEndpointResBody<EndpointPlugins>
> {
  return fetcher({
    url: urlJoin(
      appClientUrl,
      '/api/v1/plugins' satisfies EndpointPlugins['path'],
    ),
    body: {
      pluginId,
      type: 'getContent',
    } satisfies ExtractEndpointData<EndpointPlugins>,
  });
}

export async function renderTemplate({
  options,
  appClientUrl,
}: {
  options: PatternRenderData;
  appClientUrl: string;
}): Promise<KsRenderResults> {
  const data = await fetcher<KsRenderResults>({
    url: urlJoin(appClientUrl, '/api/v1/render' satisfies EndpointPaths),
    body: options,
  });
  return data;
}

export async function verifyFile({
  appClientUrl,
  payload,
}: {
  appClientUrl: string;
  payload: ExtractEndpointData<EndpointFiles>['payload'];
}) {
  const data = await fetcher<ExtractEndpointResBody<EndpointFiles>>({
    url: urlJoin(appClientUrl, '/api/v1/files' satisfies EndpointFiles['path']),
    body: {
      type: 'verify',
      payload,
    } satisfies ExtractEndpointData<EndpointFiles>,
  });
  return data.payload;
}

export async function getDiscovery({
  appClientUrl,
  authHeader,
}: {
  appClientUrl: string;
  authHeader: string;
}): Promise<
  | {
      type: 'success';
      data: RouteDiscovery['output'];
    }
  | { type: 'routeNotAvailable' }
> {
  try {
    const headers: Record<string, string> = {};
    if (authHeader) {
      headers.authorization = authHeader;
    }
    const data = await fetcher<RouteDiscovery['output']>({
      url: urlJoin(
        appClientUrl,
        '/api/v2/discovery' satisfies RouteDiscovery['path'],
      ),
      headers,
    });
    if (!data) {
      throw new Error('No result from discovery');
    }
    return {
      type: 'success',
      data,
    };
  } catch (error) {
    if (error instanceof FetcherError && error.status === 404) {
      return {
        type: 'routeNotAvailable',
      };
    }
    // being extra cautious here to avoid throwing an error
    return { type: 'routeNotAvailable' };
  }
}

export async function inspectTemplate({
  appClientUrl,
  templateInfo,
  authHeader,
}: {
  appClientUrl: string;
  templateInfo: RouteInspect['input'];
  authHeader: string;
}): Promise<RouteInspect['output']> {
  const headers: Record<string, string> = {};
  if (authHeader) {
    headers.authorization = authHeader;
  }
  return fetcher({
    url: urlJoin(
      appClientUrl,
      '/api/v2/inspect' satisfies RouteInspect['path'],
    ),
    query: templateInfo,
    headers,
  });
}
